"use client";

import { $getClipboardDataFromSelection } from "@lexical/clipboard";
import type { LexicalEditor } from "lexical";
import {
  $createNodeSelection,
  $createRangeSelection,
  $getNodeByKey,
  $getSelection,
  $isElementNode,
  $isParagraphNode,
  $setSelection,
} from "lexical";
import { $isHeadingNode } from "@lexical/rich-text";
import { $isListItemNode } from "@lexical/list";

import { $isImageBlockNode } from "../nodes/ImageBlockNode";

const LEXICAL_MIME = "application/x-lexical-editor";

function buildClipboardBlobs(clip: Record<string, string>, includeLexical: boolean): Record<string, Blob> {
  const out: Record<string, Blob> = {
    "text/plain": new Blob([clip["text/plain"] ?? ""], { type: "text/plain" }),
  };
  if (includeLexical && clip[LEXICAL_MIME]) {
    out[LEXICAL_MIME] = new Blob([clip[LEXICAL_MIME]!], { type: LEXICAL_MIME });
  }
  if (clip["text/html"]) {
    out["text/html"] = new Blob([clip["text/html"]], { type: "text/html" });
  }
  return out;
}

function tryNavigatorWriteBlobs(blobs: Record<string, Blob>): Promise<void> {
  try {
    return navigator.clipboard.write([new ClipboardItem(blobs)]);
  } catch {
    return Promise.reject(new Error("ClipboardItem"));
  }
}

export function hasRichTextLexicalPayload(clip: Record<string, string>): boolean {
  return (clip[LEXICAL_MIME] ?? "").length > 0;
}

/**
 * `NodeSelection` 只包含块壳子时，Lexical 的 `$getClipboardDataFromSelection` 不会序列化内部 TextNode
 * （`extractWithChild` 默认 false），导致标题/段落剪切板只有「壳」无正文。应对段落/标题/列表项使用
 * `RangeSelection`（与拖选一致）；图片仍为 `NodeSelection`。
 */
function setLexicalSelectionForHandleClipboard(keys: string[]): boolean {
  const validKeys = keys.filter((k) => $getNodeByKey(k) != null);
  if (validKeys.length === 0) return false;

  if (validKeys.length === 1) {
    const k0 = validKeys[0];
    const node = $getNodeByKey(k0)!;
    if ($isImageBlockNode(node)) {
      const sel = $createNodeSelection();
      sel.add(k0);
      $setSelection(sel);
      return true;
    }
    if (
      $isElementNode(node) &&
      ($isParagraphNode(node) || $isHeadingNode(node) || $isListItemNode(node))
    ) {
      node.select(0, node.getChildrenSize());
      return true;
    }
    const sel = $createNodeSelection();
    sel.add(k0);
    $setSelection(sel);
    return true;
  }

  const nodes = validKeys.map((k) => $getNodeByKey(k)!);
  if (nodes.some((n) => $isImageBlockNode(n))) {
    const sel = $createNodeSelection();
    for (const k of validKeys) sel.add(k);
    $setSelection(sel);
    return true;
  }

  const allRangeBlocks = nodes.every(
    (n) =>
      $isElementNode(n) &&
      ($isParagraphNode(n) || $isHeadingNode(n) || $isListItemNode(n)),
  );
  if (!allRangeBlocks) {
    const sel = $createNodeSelection();
    for (const k of validKeys) sel.add(k);
    $setSelection(sel);
    return true;
  }

  const sorted = [...validKeys].sort((ka, kb) => {
    if (ka === kb) return 0;
    const a = $getNodeByKey(ka)!;
    const b = $getNodeByKey(kb)!;
    return a.isBefore(b) ? -1 : 1;
  });
  const first = $getNodeByKey(sorted[0])!;
  const last = $getNodeByKey(sorted[sorted.length - 1])!;
  const range = $createRangeSelection();
  range.anchor.set(first.getKey(), 0, "element");
  const focusOffset = $isElementNode(last) ? last.getChildrenSize() : 0;
  range.focus.set(last.getKey(), focusOffset, "element");
  $setSelection(range);
  return true;
}

/**
 * 与键盘 ⌘C 等同链路：`document.execCommand('copy')` 触发带 `clipboardData` 的 `copy`。
 * 须在用户手势内**同步**调用；`discrete: true` 保证提交后再 exec。
 */
export function tryNativeExecCopyForBlockKeys(editor: LexicalEditor, keys: string[]): boolean {
  if (keys.length === 0) return false;
  const root = editor.getRootElement();
  if (root == null) return false;

  let ok = false;
  editor.update(
    () => {
      ok = setLexicalSelectionForHandleClipboard(keys);
    },
    { discrete: true },
  );

  if (!ok) return false;

  editor.focus();
  root.focus();
  try {
    return document.execCommand("copy");
  } catch {
    return false;
  }
}

/**
 * 在**同一轮用户手势**内同步发起 `navigator.clipboard.write`（用 `void` 接 Promise，不要先 `await` 再写）。
 * 部分浏览器不支持自定义 MIME，`ClipboardItem` 失败时回退为仅 html/plain。
 */
export function startRichTextClipboardWrite(clip: Record<string, string>): void {
  const lexical = clip[LEXICAL_MIME] ?? "";
  if (!lexical) return;

  void tryNavigatorWriteBlobs(buildClipboardBlobs(clip, true))
    .catch(() => tryNavigatorWriteBlobs(buildClipboardBlobs(clip, false)))
    .catch(() => navigator.clipboard.writeText(clip["text/plain"] ?? ""));
}

/**
 * 手柄菜单复制/剪切：`ClipboardItem` 带自定义 MIME 时，多数浏览器在后续 Cmd+V 的 `paste` 里
 * **拿不到** `application/x-lexical-editor`，甚至 `text/html` 也被抑制。仅写入 `text/html` + `text/plain`
 * 时 Lexical 走 HTML 分支即可还原图片块等（见 `ImageBlockNode.importDOM`）。
 */
export function startRichTextMenuClipboardWrite(clip: Record<string, string>): void {
  const plain = clip["text/plain"] ?? "";
  const html = clip["text/html"] ?? "";
  if (html.length > 0 || plain.length > 0) {
    void tryNavigatorWriteBlobs(buildClipboardBlobs(clip, false)).catch(() =>
      navigator.clipboard.writeText(plain),
    );
    return;
  }
  if (hasRichTextLexicalPayload(clip)) {
    startRichTextClipboardWrite(clip);
  }
}

/** 由 `$getClipboardDataFromSelection` 得到的条目写入剪贴板（键盘路径用 `void` 调用即可）。 */
export function writeRichTextClipboardFromData(data: Record<string, string>): Promise<boolean> {
  const lexical = data[LEXICAL_MIME] ?? "";
  if (!lexical) return Promise.resolve(false);

  return tryNavigatorWriteBlobs(buildClipboardBlobs(data, true))
    .then(() => true)
    .catch(() => tryNavigatorWriteBlobs(buildClipboardBlobs(data, false)).then(() => true))
    .catch(() => navigator.clipboard.writeText(data["text/plain"] ?? "").then(() => true))
    .catch(() => false);
}

/** 在 `editor.update` 内采集块对应的 Lexical/HTML/纯文本剪贴板字段（须同步调用）。 */
export function collectRichTextClipboardForBlockKeys(editor: LexicalEditor, keys: string[]): Record<string, string> {
  const clip: Record<string, string> = {};
  if (keys.length === 0) return clip;

  editor.update(() => {
    if (!setLexicalSelectionForHandleClipboard(keys)) return;
    const sel = $getSelection();
    if (sel == null) return;
    Object.assign(clip, $getClipboardDataFromSelection(sel) as unknown as Record<string, string>);
  });

  return clip;
}

/**
 * 将指定块 key 写入系统剪贴板（供仍需 Promise 形态的调用方；手柄菜单请用 collect + startRichTextClipboardWrite）。
 */
export async function writeRichTextClipboardForBlockKeys(
  editor: LexicalEditor,
  keys: string[],
): Promise<boolean> {
  const clip = collectRichTextClipboardForBlockKeys(editor, keys);
  return writeRichTextClipboardFromData(clip);
}
